<script setup lang="ts">
// {{{ imports
import { toTypedSchema } from '@vee-validate/zod'
import {
  AliyahType,
  BarMitzvahType,
  HaftarahMelodyType,
  HaftarahType,
  ParashahType,
  PracticeStatsType,
  PronunciationType,
  SentenceGroupMelodyType,
  SentenceGroupType,
  TextPracticeStatsType,
  TorahMelodyType,
  WeekStatsType,
} from 'types/types'
import { useField, useForm } from 'vee-validate'
import { computed, onMounted, reactive, Ref, ref, watch, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { z as zod } from 'zod'

import DatePicker from '@/components/DatePicker.vue'
import GroupedSentenceGroupSelect from '@/components/GroupedSentenceGroupSelect.vue'
import HaftarahSelect from '@/components/HaftarahSelect.vue'
import ParashahSelect from '@/components/ParashahSelect.vue'
import StudentSelect from '@/components/StudentSelect.vue'
import UserIdentityFields from '@/components/UserIdentityFields.vue'
import { formattedAliyahForTeacher, formattedAliyahSimple, formatTimeAgo, hebrewPlural } from '@/composables/format'
import { useZodValidation } from '@/composables/zodValidation'
import {
  CHART_BAR_MAX_WIDTH_PX,
  CHART_BAR_MIN_HEIGHT_PX,
  CHART_BAR_MIN_WIDTH_PX,
  CHART_BAR_SCALE_HEIGHT_PX,
  CHART_DAYS_DEFAULT,
  CHART_DAYS_MAX,
  CHART_DAYS_STEP,
  CHART_HEIGHT_PX,
  CHART_SLIDER_MAX_WIDTH_PX,
} from '@/constants/practiceConstants'
import store from '@/store'
import { sortAliyotByRank } from '@/util/Aliyah'
// }}}
// {{{ consts
const state = reactive<StudentsData>({
  barMitzvah: null,
  bogusHaftarahMelody: null,
  bogusPronunciation: null,
  bogusSentenceGroupMelody: null,
  bogusTorahMelody: null,
  editExistingUser: false,
  formVisible: false,
  selectedAliyot: new Array<AliyahType>(),
  selectedHaftarah: null,
  selectedParashah: null,
  selectedSentenceGroups: new Array<SentenceGroupType>(),
  showHiddenBarMitzvot: false,
  showSpinner: false,
})

const { nonEmptyString } = useZodValidation()
const logger = useLogger()
const { errors, handleSubmit, meta, resetForm } = useForm({
  validationSchema: toTypedSchema(
    zod.object({
      date: zod.string().refine(
        (value) => {
          const result = /^\d{4}-\d{2}-\d{2}$/.test(value)

          if (!result) {
            return false
          }

          const [year, month, day] = value.split('-').map(Number)
          if (year <= 1900) {
            return false
          }
          if (month < 1 || month > 12) {
            return false
          }

          const date = new Date(year, month - 1, day)
          if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
            return false // Date validation against invalid days of the month
          }

          return true
        },
        {
          message: 'Date must be in YYYY-MM-DD format, year > 2000, MM between 01-12, and DD valid for given month and year',
        },
      ),
      email: zod.string().email(),
      firstName: nonEmptyString,
      haftarahMelody: zod.object({ id: zod.string() }),
      hidden: zod.boolean().default(false),
      kippahReminder: zod.boolean().default(false),
      lastName: nonEmptyString,
      practiceTracking: zod.boolean().default(true),
      pronunciation: zod.object({ id: zod.string() }),
      sentenceGroupMelody: zod.object({ id: zod.string() }),
      torahMelody: zod.object({ id: zod.string() }),
      username: nonEmptyString,
    }),
  ),
})
const { value: date } = useField('date')
const { value: email } = useField<string>('email')
const { value: firstName } = useField<string>('firstName')
const { value: haftarahMelody } = useField('haftarahMelody')
const { value: hidden } = useField('hidden')
const { value: kippahReminder } = useField('kippahReminder')
const { value: practiceTracking } = useField('practiceTracking')
const { value: lastName } = useField<string>('lastName')
const { value: pronunciation } = useField('pronunciation')
const { value: sentenceGroupMelody } = useField('sentenceGroupMelody')
const { value: torahMelody } = useField('torahMelody')
const { value: username } = useField<string>('username')
// }}}
// {{{ interfaces
interface StudentsData {
  barMitzvah: BarMitzvahType | null
  editExistingUser: boolean
  formVisible: boolean
  selectedAliyot: Array<AliyahType>
  selectedHaftarah: HaftarahType | null
  selectedParashah: ParashahType | null
  selectedSentenceGroups: Array<SentenceGroupType>
  showHiddenBarMitzvot: boolean
  showSpinner: boolean

  // these exist to circumvent a build error that disallows setting vee-validate handled fields directly
  // these combine with 4x watchEffect below
  bogusPronunciation: PronunciationType | null
  bogusTorahMelody: TorahMelodyType | null
  bogusHaftarahMelody: HaftarahMelodyType | null
  bogusSentenceGroupMelody: SentenceGroupMelodyType | null
}
// }}}

onMounted((): void => {
  // {{{ init
  store.dispatch.teacher.setLogger(logger)
  store.dispatch.user.fetchUser()
  store.dispatch.teacher.fetchLightBarMitzvot()
  store.dispatch.teacher.fetchSentenceGroups({ mine: false })
  store.dispatch.teacher.fetchPracticeOverview()
  // }}}
  // {{{ watch state.showHiddenBarMitzvot
  // we can't be showing the current bar mitzvah if we're asked to only show non-hidden barmitzvot
  watch(
    () => state.showHiddenBarMitzvot,
    () => {
      if (!state.showHiddenBarMitzvot && state.barMitzvah?.hidden && barMitzvahSelect.value) {
        barMitzvahSelect.value.clear()
      }
    },
  )
  // }}}
  // {{{ watch state.selectedParashah
  watch(
    () => state.selectedParashah,
    () => {
      store.dispatch.teacher.fetchAliyot({
        mine: false,
        parashah: state.selectedParashah as ParashahType,
      })
    },
  )
  // }}}
  // {{{ watch state.selectedHaftarah
  watch(
    () => state.selectedHaftarah,
    async () => {
      if (!state.barMitzvah || state.barMitzvah.aliyot === undefined) {
        return
      }

      let update: Promise<boolean> | null = null
      if (state.selectedHaftarah) {
        if (state.barMitzvah.haftarah !== state.selectedHaftarah) {
          update = store.dispatch.teacher.setHaftarahToBarMitzvah({ barMitzvah: state.barMitzvah, haftarah: state.selectedHaftarah })
        }
      } else if (state.barMitzvah.haftarah) {
        update = store.dispatch.teacher.unsetHaftarahFromBarMitzvah({ barMitzvah: state.barMitzvah })
      }

      if (update && (await update)) {
        store.dispatch.snackbar.add({ message: 'Haftarah updated.', state: 'primary' })
      }
    },
  )
  // }}}
  // {{{ watch state.barMitzvah
  watch(
    () => state.barMitzvah,
    async (newValue, oldValue) => {
      if (!state.barMitzvah) {
        state.selectedParashah = null
        state.selectedAliyot = new Array<AliyahType>()
        state.selectedSentenceGroups = new Array<SentenceGroupType>()
        state.selectedHaftarah = null
        return
      }

      // StudentSelect provides "light" bar mitzvot (only student/date/hidden).
      // Fetch the full version with aliyot, haftarah, sentenceGroups, etc.
      if (state.barMitzvah.aliyot === undefined) {
        state.showSpinner = true
        // Resolve the "light" bar mitzvah to the full object. The returned reference matches
        // StudentSelect's lightBarMitzvot items, keeping Vuetify's v-autocomplete model in sync.
        const fullBm = await store.dispatch.teacher.fetchFullBarMitzvah(state.barMitzvah)
        if (fullBm) {
          state.barMitzvah = fullBm // re-triggers this watcher with full data
        } else {
          state.showSpinner = false
        }
        return
      }

      if (state.barMitzvah.aliyot.length) {
        state.selectedParashah = state.barMitzvah.aliyot[0].parashah
        state.selectedAliyot = [...sortedAliyot([...state.barMitzvah.aliyot])]
      } else {
        // do not unset selectedParashah when the last aliyah is removed; we
        // don't want the parashah selection to get unset; but when we're changing students, then do wipe the old parashah
        if (newValue !== oldValue) {
          state.selectedParashah = null
        }
        state.selectedAliyot.splice(0, state.selectedAliyot.length)
      }
      state.selectedHaftarah = state.barMitzvah.haftarah as HaftarahType
      state.selectedSentenceGroups = [...state.barMitzvah.sentenceGroups].sort((a, b) =>
        (a.reading.description ?? '').localeCompare(b.reading.description ?? ''),
      )
      state.showSpinner = false

      // Fetch practice stats for selected student
      store.dispatch.teacher.fetchPracticeStats({ studentId: state.barMitzvah.student.id })
    },
    { deep: true },
  )
  // }}}
  // {{{ watch state.selectedAliyot
  watch(
    () => state.selectedAliyot,
    (newVal) => {
      if (!state.barMitzvah || state.barMitzvah.aliyot === undefined) {
        logger.debug('unexpected !state.barMitzvah in changeAliyot')
        return
      }
      logger.info('changeAliyot')
      const addedAliyot = newVal.filter((aliyah: AliyahType) => state.barMitzvah!.aliyot.indexOf(aliyah) < 0)
      addedAliyot.forEach((aliyah: AliyahType) => {
        store.dispatch.teacher.addAliyahToBarMitzvah({
          aliyah,
          barMitzvah: state.barMitzvah as BarMitzvahType,
        })
      })

      const removedAliyot = state.barMitzvah.aliyot.filter((aliyah: AliyahType) => newVal.indexOf(aliyah) < 0)
      removedAliyot.forEach((aliyah: AliyahType) => {
        store.dispatch.teacher.deleteAliyahFromBarMitzvah({ aliyah, barMitzvah: state.barMitzvah as BarMitzvahType })
      })

      if (addedAliyot.length || removedAliyot.length) {
        store.dispatch.snackbar.add({ message: 'Aliyah updated.', state: 'primary' })
      }
    },
  )
  // }}}
  // {{{ state.selectedSentenceGroups
  watch(
    () => state.selectedSentenceGroups,
    (newVal) => {
      if (!state.barMitzvah || state.barMitzvah.sentenceGroups === undefined) {
        logger.debug('unexpected !state.barMitzvah in addSentenceGroup')
        return
      }

      const addedSentenceGroups = newVal.filter(
        (sentenceGroup: SentenceGroupType) => state.barMitzvah!.sentenceGroups.indexOf(sentenceGroup) < 0,
      )
      addedSentenceGroups.forEach((sentenceGroup: SentenceGroupType) => {
        store.dispatch.teacher.addSentenceGroupToBarMitzvah({
          barMitzvah: state.barMitzvah as BarMitzvahType,
          sentenceGroup,
        })
      })

      const removedSentenceGroups = state.barMitzvah.sentenceGroups.filter(
        (sentenceGroup: SentenceGroupType) => newVal.indexOf(sentenceGroup) < 0,
      )
      removedSentenceGroups.forEach((sentenceGroup: SentenceGroupType) => {
        store.dispatch.teacher.deleteSentenceGroupFromBarMitzvah({
          barMitzvah: state.barMitzvah as BarMitzvahType,
          sentenceGroup,
        })
      })

      if (addedSentenceGroups.length || removedSentenceGroups.length) {
        store.dispatch.snackbar.add({ message: "B'rachot / Songs updated.", state: 'primary' })
      }
    },
  )
  // }}}
  // {{{ watchEffect state.bogusPronunciation
  watchEffect(() => {
    pronunciation.value = state.bogusPronunciation
  })
  // }}}
  // {{{ watchEffect state.bogusTorahMelody
  watchEffect(() => {
    torahMelody.value = state.bogusTorahMelody
  })
  // }}}
  // {{{ watchEffect state.bogusHaftarahMelody
  watchEffect(() => {
    haftarahMelody.value = state.bogusHaftarahMelody
  })
  // }}}
  // {{{ watchEffect state.bogusSentenceGroupMelody
  watchEffect(() => {
    sentenceGroupMelody.value = state.bogusSentenceGroupMelody
  })
  // }}}
})

// computed functions
// {{{ computed selectedAliyot
const selectableAliyot = computed<Readonly<Array<AliyahType>>>((): Readonly<Array<AliyahType>> => {
  if (!state.selectedParashah) {
    return []
  }

  const r = store.getters.teacher.aliyot.filter((aliyah: AliyahType) => {
    return state.selectedParashah && aliyah.parashah.reading.id === state.selectedParashah.reading.id
  })

  return r as Readonly<Array<AliyahType>>
})
// }}}
// {{{ computed barMitzvahReady — true only when the full (non-light) bar mitzvah is loaded
const barMitzvahReady = computed(() => !!state.barMitzvah && state.barMitzvah.aliyot !== undefined)
// }}}
// {{{ computed parashah
const parashah = computed<ParashahType | null>((): ParashahType | null => {
  return state.barMitzvah?.aliyot?.[0]?.parashah ?? null
})
// }}}
// {{{ computed torahMelodies
const torahMelodies = computed<Array<TorahMelodyType>>(() => [...store.getters.user.userTorahMelodies])
// }}}
// {{{ computed haftarahMelodies
const haftarahMelodies = computed<Array<HaftarahMelodyType>>(() => [...store.getters.user.userHaftarahMelodies])
// }}}
// {{{ computed sentenceGroupMelodies
const sentenceGroupMelodies = computed<Array<SentenceGroupMelodyType>>(() => [...store.getters.user.userSentenceGroupMelodies])
// }}}
// {{{ computed pronunciations
const pronunciations = computed<Array<PronunciationType>>(() => [...store.getters.user.userPronunciations])
// }}}
// {{{ computed practiceStats
const practiceStats = computed<PracticeStatsType | null>(() => store.getters.teacher.practiceStats)
// }}}
// {{{ practice chart — slider, visible history, scaling
const chartDays = ref(CHART_DAYS_DEFAULT)

function resetChartDays(): void {
  chartDays.value = CHART_DAYS_DEFAULT
}

/** Slice weeklyHistory to only the weeks within the selected time window, filtering nulls. */
const visibleHistory = computed<WeekStatsType[]>(() => {
  const history = (practiceStats.value?.weeklyHistory ?? []).filter((w): w is WeekStatsType => w != null)
  if (!history.length) {
    return []
  }
  const weeksToShow = Math.max(1, Math.ceil(chartDays.value / 7))
  // weeklyHistory comes newest-first from backend; take first N then reverse for display (oldest left)
  return [...history.slice(0, weeksToShow)].reverse()
})

/** Text breakdown with nulls filtered out. */
const visibleTextBreakdown = computed<TextPracticeStatsType[]>(() =>
  (practiceStats.value?.textBreakdown ?? []).filter((t): t is TextPracticeStatsType => t != null),
)

const weeklyBarMaxMinutes = computed(() => {
  if (!visibleHistory.value.length) {
    return 1
  }
  return Math.max(1, ...visibleHistory.value.map((w) => w.totalMinutes ?? 0))
})

/** Format a week_start date as a short label like "2/3" (month/day). */
function formatWeekLabel(weekStart: string | null | undefined): string {
  if (!weekStart) {
    return ''
  }
  const d = new Date(weekStart + 'T00:00:00')
  return `${d.getMonth() + 1}/${d.getDate()}`
}
// }}}
// {{{ Ref barMitzvahSelect
const barMitzvahSelect: Ref<typeof StudentSelect | null> = ref(null)
// }}}

// {{{ sortedAliyot
function sortedAliyot(aliyot: Readonly<Array<AliyahType>>): Readonly<Array<AliyahType>> {
  return sortAliyotByRank(aliyot)
}
// }}}
// {{{ editBarMitzvah
function editBarMitzvah() {
  if (!state.barMitzvah) {
    logger.error('unexpected null state.barMitzvah in editBarMitzvah')
    return
  }

  state.editExistingUser = true

  date.value = state.barMitzvah.date
  email.value = state.barMitzvah.student.email
  firstName.value = state.barMitzvah.student.firstName
  hidden.value = state.barMitzvah.hidden
  kippahReminder.value = state.barMitzvah.kippahReminder
  practiceTracking.value = state.barMitzvah.practiceTracking ?? true
  lastName.value = state.barMitzvah.student.lastName
  username.value = state.barMitzvah.student.username

  // see documentation at declaration
  state.bogusHaftarahMelody = state.barMitzvah.haftarahMelody ?? ({} as HaftarahMelodyType)
  state.bogusPronunciation = state.barMitzvah.pronunciation ?? ({} as PronunciationType)
  state.bogusSentenceGroupMelody = state.barMitzvah.sentenceGroupMelody ?? ({} as SentenceGroupMelodyType)
  state.bogusTorahMelody = state.barMitzvah.torahMelody ?? ({} as TorahMelodyType)

  state.formVisible = true
}
// }}}
// {{{ confirmAndDeleteBarMitzvah
function confirmAndDeleteBarMitzvah() {
  store.dispatch.dialog.show({
    isRejectable: true,
    message: 'Are you sure you want to delete this student?',
    onAccept: async () => {
      const deleted = await store.dispatch.teacher.deleteBarMitzvah({
        barMitzvah: state.barMitzvah as BarMitzvahType,
      })
      if (deleted) {
        state.barMitzvah = null
        store.dispatch.snackbar.add({ message: 'Deleted.', state: 'primary' })
      }
    },
    title: 'Confirm',
  })
}
// }}}
// {{{ addStudent
function addStudent() {
  customResetForm()
  state.editExistingUser = false
  state.formVisible = true
}
// }}}
// {{{ customResetForm
const customResetForm = () => {
  resetForm()
  autoSelectSingle(torahMelodies, (v) => {
    state.bogusTorahMelody = v as TorahMelodyType
  })
  autoSelectSingle(haftarahMelodies, (v) => {
    state.bogusHaftarahMelody = v as HaftarahMelodyType
  })
  autoSelectSingle(sentenceGroupMelodies, (v) => {
    state.bogusSentenceGroupMelody = v as SentenceGroupMelodyType
  })
  autoSelectSingle(pronunciations, (v) => {
    state.bogusPronunciation = v as PronunciationType
  })
  date.value = new Date().toISOString().substring(0, 10)
}
// }}}
// {{{ handleSubmit / addOrUpdateBarMitzvahWrapper
const addOrUpdateBarMitzvahWrapper = handleSubmit((values) => {
  store.dispatch.teacher
    .addOrUpdateBarMitzvah({
      date: values.date ?? '',
      haftarahMelodyID: values.haftarahMelody ? parseInt((values.haftarahMelody as HaftarahMelodyType).id) : -1,
      hidden: values.hidden === undefined ? false : values.hidden,
      kippahReminder: values.kippahReminder === undefined ? false : values.kippahReminder,
      practiceTracking: values.practiceTracking === undefined ? true : values.practiceTracking,
      pronunciationID: pronunciation.value ? parseInt((pronunciation.value as PronunciationType).id) : -1,
      sentenceGroupMelodyID: sentenceGroupMelody.value ? parseInt((sentenceGroupMelody.value as SentenceGroupMelodyType).id) : -1,
      torahMelodyID: torahMelody.value ? parseInt((torahMelody.value as TorahMelodyType).id) : -1,
      update: state.editExistingUser,
      user: {
        email: values.email ?? '',
        firstName: values.firstName ?? '',
        lastName: values.lastName ?? '',
        username: values.username ?? '',
      },
    })
    .then((status: boolean) => {
      if (status) {
        store.dispatch.snackbar.add({ message: state.editExistingUser ? 'Student updated.' : 'Student added.', state: 'primary' })
        state.formVisible = false
        store.dispatch.teacher
          .fetchLightBarMitzvot()
          .then(() => {
            const bm = store.getters.teacher.lightBarMitzvot.find((barMitzvah: BarMitzvahType) => {
              return barMitzvah.student.username === values.username
            })
            state.barMitzvah = bm ?? null
          })
          .then(() => {
            if (!state.barMitzvah) {
              logger.error('unexpected null state.barMitzvah in addOrUpdateBarMitzvah (2)')
              return
            }
            if (!state.showHiddenBarMitzvot && state.barMitzvah.hidden && barMitzvahSelect.value) {
              barMitzvahSelect.value.clear()
            }
          })
      }
    })
})
// }}}

// {{{ autoSelectSingle
function autoSelectSingle<T>(options: { value: T[] }, setter: (val: T) => void) {
  if (options.value.length === 1) {
    setter(options.value[0])
  }
}
// }}}
</script>

<template>
  <!-- {{{ template -->
  <!-- edit a custom text -->
  <!-- @shown="focusDescription" -->
  <v-dialog
    id="newStudent"
    ref="newStudent"
    v-model="state.formVisible"
    class="mb-4 pb-4"
    content-class="bmt-popup-dialog"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.4"
    @keydown.esc="state.formVisible = false"
  >
    <v-form @reset.prevent="customResetForm" @submit="addOrUpdateBarMitzvahWrapper">
      <v-card>
        <v-card-title> {{ state.editExistingUser ? 'Update' : 'Add' }} user </v-card-title>
        <v-card-text>
          <user-identity-fields
            v-model:email="email"
            v-model:first-name="firstName"
            v-model:last-name="lastName"
            v-model:username="username"
            :errors="errors"
            :username-disabled="!!state.editExistingUser"
          />
          <v-row align-h="start">
            <v-col md="2">
              <date-picker :model-value="date as string" @update:model-value="date = $event" />
            </v-col>

            <v-col md="1" />
            <v-col md="2">
              <v-checkbox v-model="hidden" dense label="Hide student" />
            </v-col>
            <v-col md="2">
              <v-checkbox v-model="kippahReminder" dense label="Kippah reminder" />
            </v-col>
            <v-col md="2">
              <v-checkbox v-model="practiceTracking" dense label="Practice tracking" />
            </v-col>
          </v-row>
          <v-row v-if="pronunciations">
            <v-col md="3">
              <v-select
                v-model="state.bogusPronunciation as PronunciationType"
                item-title="description"
                :items="pronunciations"
                label="Pronunciation"
                return-object
                variant="underlined"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col v-if="torahMelodies" md="3">
              <v-select
                v-model="state.bogusTorahMelody as TorahMelodyType"
                item-title="description"
                :items="torahMelodies"
                label="Melodic style (Torah)"
                return-object
                variant="underlined"
              />
            </v-col>
            <v-col v-if="haftarahMelodies" md="3">
              <v-select
                v-model="state.bogusHaftarahMelody as HaftarahMelodyType"
                item-title="description"
                :items="haftarahMelodies"
                label="Melodic style (haftarah)"
                return-object
                variant="underlined"
              />
            </v-col>
          </v-row>
          <v-row v-if="sentenceGroupMelodies">
            <v-col md="3">
              <v-select
                v-model="state.bogusSentenceGroupMelody as SentenceGroupMelodyType"
                item-title="description"
                :items="sentenceGroupMelodies"
                label="Melodic style (other texts)"
                return-object
                variant="underlined"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="2">
              <v-btn block color="primary" :disabled="!meta.valid || store.getters.teacher.updating" type="submit">
                {{ state.editExistingUser ? 'Update' : 'Add' }}
                <v-progress-circular v-if="store.getters.teacher.updating" class="ml-4" indeterminate />
              </v-btn>
            </v-col>
            <v-col md="2">
              <v-btn block variant="plain" @click="state.formVisible = false"> Cancel </v-btn>
            </v-col>
            <v-col v-if="!state.editExistingUser" md="2">
              <v-btn block color="warning" :disabled="store.getters.teacher.updating" type="reset" @click="customResetForm"> Reset </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
  </v-dialog>

  <!-- Table of students -->
  <v-row>
    <v-col md="4">
      <student-select
        ref="barMitzvahSelect"
        v-model:barMitzvah="state.barMitzvah"
        :bar-mitzvah-clearable="true"
        :external-set="true"
        :include-hidden="state.showHiddenBarMitzvot"
      />
    </v-col>

    <v-col v-if="barMitzvahReady" class="mt-4" md="2">
      <v-btn class="mr-4" color="primary" icon size="small" @click="editBarMitzvah">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn class="mr-4" color="error" icon size="small" @click="confirmAndDeleteBarMitzvah">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
      <v-progress-circular v-if="store.getters.teacher.updating" indeterminate />
    </v-col>

    <v-col v-if="!state.barMitzvah && !state.showSpinner" md="2">
      <v-switch v-model="state.showHiddenBarMitzvot" color="primary" label="include hidden" />
    </v-col>
  </v-row>

  <v-row v-if="!state.barMitzvah">
    <v-col>
      <v-btn class="px-6" color="primary" @click="addStudent"> Add student </v-btn>
    </v-col>
  </v-row>

  <v-row v-if="barMitzvahReady">
    <v-col>
      <v-btn @click="() => (state.barMitzvah = null)">Clear</v-btn>
    </v-col>
  </v-row>

  <v-row v-if="state.showSpinner">
    <v-col>
      <v-progress-circular indeterminate />
    </v-col>
  </v-row>

  <v-row v-if="barMitzvahReady">
    <v-col md="4">
      <parashah-select
        v-model:parashah="state.selectedParashah"
        :clearable="!state.selectedAliyot.length"
        :disabled="!!state.selectedAliyot.length"
        :external-set="true"
        :only="parashah ? [parashah] : null"
      />
    </v-col>

    <v-col v-if="state.selectedParashah" md="4">
      <v-autocomplete
        v-model="state.selectedAliyot"
        chips
        :error="!state.selectedAliyot.length"
        :error-messages="state.selectedAliyot.length ? [] : ['You must choose at least one aliyah']"
        :item-title="
          (aliyah: AliyahType) => {
            return formattedAliyahForTeacher(aliyah)
          }
        "
        :items="sortedAliyot(selectableAliyot)"
        :label="hebrewPlural(state.barMitzvah?.aliyot?.length ?? 0, 'Aliyah')"
        :loading="!selectableAliyot || !selectableAliyot.length"
        multiple
        return-object
        variant="underlined"
      >
        <template #chip="{ props, item }">
          <v-chip v-bind="props" closable size="small" :text="formattedAliyahSimple(item.raw)" />
        </template>
      </v-autocomplete>
    </v-col>
  </v-row>

  <!-- Haftarah dropdown -->
  <v-row v-if="barMitzvahReady && (state.barMitzvah!.haftarah || state.selectedParashah)">
    <v-col md="4">
      <haftarah-select
        v-model:haftarah="state.selectedHaftarah"
        :clearable="true"
        :external-set="true"
        :only="!state.selectedParashah ? ([state.barMitzvah!.haftarah] as Array<HaftarahType>) : null"
        :parashah="state.selectedParashah"
      />
    </v-col>
  </v-row>

  <!-- SentenceGroup dropdown -->
  <v-row v-if="barMitzvahReady">
    <v-col md="8">
      <grouped-sentence-group-select
        v-model="state.selectedSentenceGroups"
        chips
        label="B'rachot / Songs"
        multiple
        :sentence-groups="store.getters.teacher.sentenceGroups"
      >
        <template #chip="{ props, item }">
          <v-chip v-bind="props" closable size="small" :text="item.raw.reading.description as string" />
        </template>
      </grouped-sentence-group-select>
    </v-col>
  </v-row>

  <!-- Practice Activity Card -->
  <v-row v-if="barMitzvahReady && practiceStats">
    <v-col md="6">
      <v-card class="mt-2" variant="outlined">
        <v-card-title class="text-subtitle-1">Practice Activity</v-card-title>
        <v-card-text>
          <!-- Tracking paused notice -->
          <v-alert v-if="!practiceStats.trackingEnabled" class="mb-4" density="compact" type="info" variant="tonal">
            Tracking paused by student{{
              practiceStats.trackingPausedAt ? ` since ${new Date(practiceStats.trackingPausedAt).toLocaleDateString()}` : ''
            }}
          </v-alert>

          <!-- Summary row -->
          <v-row>
            <v-col cols="auto">
              <div class="text-body-2 text-medium-emphasis">Last practiced</div>
              <div class="text-h6">{{ formatTimeAgo(practiceStats.lastPracticedAt) }}</div>
            </v-col>
            <v-col v-if="practiceStats.currentWeek" cols="auto">
              <div class="text-body-2 text-medium-emphasis">This week</div>
              <div class="text-h6">
                {{ practiceStats.currentWeek.totalMinutes }} min &middot; {{ practiceStats.currentWeek.sessionCount }} sessions &middot;
                {{ practiceStats.currentWeek.activeDays }} days
              </div>
            </v-col>
          </v-row>

          <!-- Weekly trend bar chart -->
          <div v-if="practiceStats.weeklyHistory?.length" class="mt-6">
            <div class="d-flex align-center mb-3">
              <span class="text-body-2 text-medium-emphasis mr-4">Practice history</span>
              <v-slider
                v-model="chartDays"
                density="compact"
                hide-details
                :max="CHART_DAYS_MAX"
                :min="CHART_DAYS_STEP"
                :step="CHART_DAYS_STEP"
                :style="{ maxWidth: `${CHART_SLIDER_MAX_WIDTH_PX}px` }"
                :thumb-label="true"
                @dblclick="resetChartDays"
              />
              <span class="text-body-2 text-medium-emphasis ml-2">{{ chartDays }} days</span>
            </div>

            <!-- Y-axis label -->
            <div class="text-body-2 text-medium-emphasis mb-1">Minutes</div>

            <!-- Chart area -->
            <div
              class="d-flex align-end ga-3"
              :style="{
                height: `${CHART_HEIGHT_PX}px`,
                borderBottom: '2px solid black',
                borderLeft: '2px solid black',
                paddingLeft: '4px',
              }"
            >
              <div
                v-for="(week, i) in visibleHistory"
                :key="i"
                class="d-flex flex-column align-center"
                :style="{ flex: '1 1 0', minWidth: `${CHART_BAR_MIN_WIDTH_PX}px` }"
              >
                <!-- Value on top of bar -->
                <span class="text-body-2 text-medium-emphasis mb-1">{{ week.totalMinutes ?? 0 }}</span>
                <div
                  class="rounded-t"
                  :style="{
                    backgroundColor: i === visibleHistory.length - 1 ? 'rgb(var(--v-theme-primary))' : 'rgb(var(--v-theme-primary), 0.4)',
                    height: `${Math.max(CHART_BAR_MIN_HEIGHT_PX, ((week.totalMinutes ?? 0) / weeklyBarMaxMinutes) * CHART_BAR_SCALE_HEIGHT_PX)}px`,
                    width: '100%',
                    maxWidth: `${CHART_BAR_MAX_WIDTH_PX}px`,
                  }"
                  :title="`Week of ${week.weekStart}: ${week.totalMinutes} min`"
                />
              </div>
            </div>

            <!-- X-axis labels -->
            <div class="d-flex ga-3 mt-1">
              <div
                v-for="(week, i) in visibleHistory"
                :key="'x-' + i"
                class="text-body-2 text-medium-emphasis text-center"
                :style="{ flex: '1 1 0', minWidth: `${CHART_BAR_MIN_WIDTH_PX}px` }"
              >
                {{ formatWeekLabel(week.weekStart) }}
              </div>
            </div>
          </div>

          <!-- Text breakdown -->
          <div v-if="visibleTextBreakdown.length" class="mt-4">
            <div class="text-caption text-medium-emphasis mb-2">By text</div>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>Text</th>
                  <th class="text-right">Total (min)</th>
                  <th>Last practiced</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(text, i) in visibleTextBreakdown" :key="i">
                  <td>{{ text.description }}</td>
                  <td class="text-right">{{ text.totalMinutes }}</td>
                  <td>{{ formatTimeAgo(text.lastPracticedAt) }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- No data state -->
          <div v-if="!practiceStats.lastPracticedAt && practiceStats.trackingEnabled" class="text-body-2 text-medium-emphasis mt-2">
            No practice activity recorded yet.
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  <!-- }}} -->
</template>
